import React, { useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider } from "./context/LangContext";
import { usersAPI } from "./api";
import OAuthPage from "./pages/OAuthPage";
import AuthPage from "./pages/AuthPage";
import DashboardPage from "./pages/DashboardPage";
import TopicsPage from "./pages/TopicsPage";
import TicketsPage from "./pages/TicketsPage";
import SettingsPage from "./pages/SettingsPage";
import SignsPage from "./pages/SignsPage";
import CustomTestPage from "./pages/CustomTestPage";
import FavoritesPage from "./pages/FavoritesPage";
import HistoryPage from "./pages/HistoryPage";
import SessionsPage from "./pages/SessionsPage";
import PaymentsPage from "./pages/PaymentsPage";
import RoadmapPage from "./pages/RoadmapPage";
import MistakesPage from "./pages/MistakesPage";
import LandingPage from "./pages/LandingPage";
import TimeAttackPage from "./pages/TimeAttackPage";
import DuelPage from "./pages/DuelPage";
import ShpargalkaPage from "./pages/ShpargalkaPage";
import TestsPage from "./pages/TestsPage";
import GamesPage from "./pages/GamesPage";
import KnowledgePage from "./pages/KnowledgePage";
import LearnHubPage from "./pages/LearnHubPage";
import ContactsPage from "./pages/ContactsPage";
import TeachersPage from "./pages/TeachersPage";
import QuizPage from "./pages/QuizPage";
import AdminPage from "./pages/AdminPage";
import TeacherPanelPage from "./pages/TeacherPanelPage";
import FriendDuelPage from "./pages/FriendDuelPage";
import NotificationsPage from "./pages/NotificationsPage";
import VideosPage from "./pages/VideosPage";
import StatisticsPage from "./pages/StatisticsPage";
import AiUstozInsightsPage from "./pages/AiUstozPage/InsightsPage";
import AiUstozChatPage from "./pages/AiUstozPage/ChatPage";
import { FriendDuelProvider } from "./context/FriendDuelContext";
import FriendInviteNotification from "./components/FriendInviteNotification";
import InstallAppPrompt from "./components/InstallAppPrompt";
import TeacherDrawOverlay from "./components/TeacherDrawOverlay";
import ServerDownOverlay from "./components/ServerDownOverlay";
import { initTextScale } from "./utils/textScale";

// Saqlangan "katta-kichik" (zoom) qiymatini sahifa ochilgandayoq
// qo'llaymiz — shu bilan barcha sahifalarda bir xil ko'rinadi.
initTextScale();

// ─── Onboarding Modal ───────────────────────────────────────────
const OnboardingModal: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState<"user" | "teacher">("user");
  const [loading, setLoading] = useState(false);

  if (!user || user.is_onboarded) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await usersAPI.updateRole(selectedRole);
      updateUser({ role: selectedRole, is_onboarded: true });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}>
      <div
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 16,
          padding: 28,
          width: "100%",
          maxWidth: 320,
        }}>
        {/* Title */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>👋</div>
          <h3
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 18,
              marginBottom: 6,
            }}>
            Xush kelibsiz!
          </h3>
          <p style={{ color: "#64748B", fontSize: 13 }}>
            Platformadan qanday foydalanasiz?
          </p>
        </div>

        {/* Role buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {(["user", "teacher"] as const).map((r) => (
            <button
              key={r}
              onClick={() => setSelectedRole(r)}
              style={{
                flex: 1,
                padding: "16px 8px",
                borderRadius: 12,
                border: "1px solid",
                borderColor: selectedRole === r ? "#3B82F6" : "#1E2D45",
                background:
                  selectedRole === r ? "rgba(59,130,246,.15)" : "#0D1526",
                color: selectedRole === r ? "#3B82F6" : "#64748B",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                transition: "all .2s",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}>
              <span style={{ fontSize: 28 }}>{r === "user" ? "🎓" : "👨‍🏫"}</span>
              {r === "user" ? "O'quvchi" : "Ustoz"}
            </button>
          ))}
        </div>

        {/* Confirm */}
        <button
          onClick={handleConfirm}
          disabled={loading}
          style={{
            width: "100%",
            padding: "13px",
            borderRadius: 10,
            border: "none",
            background: "#3B82F6",
            color: "#fff",
            fontWeight: 800,
            fontSize: 14,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.8 : 1,
          }}>
          {loading ? "Saqlanmoqda..." : "Davom etish →"}
        </button>
      </div>
    </div>
  );
};

// ─── Private Route ──────────────────────────────────────────────
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token, loading, user } = useAuth();

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0A0E1A",
        }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "3px solid rgba(59,130,246,.2)",
            borderTopColor: "#3B82F6",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );

  if (!token) return <Navigate to="/auth" replace />;
  if (user?.role === "admin") return <>{children}</>;

  return (
    <>
      <OnboardingModal />
      {children}
    </>
  );
};

// ─── RootRedirect ───────────────────────────────────────────────
// "/" — allaqachon tizimga kirgan foydalanuvchi uchun avtomatik
// /dashboard'ga yo'naltiradi. Bu iOS'da "Bosh ekranga qo'shish"
// orqali o'rnatilgan ilova ochilganda muhim: manifest'ning
// start_url'i doim "/" (bitta barqaror kirish nuqtasi bo'lishi
// kerak), lekin foydalanuvchi har safar marketing sahifasini emas,
// to'g'ridan-to'g'ri o'z dashboard'ini ko'rishi kerak.
const RootRedirect: React.FC = () => {
  const { token, loading } = useAuth();

  if (loading) return null;
  if (token) return <Navigate to="/dashboard" replace />;

  return <LandingPage />;
};

// ─── ScrollToTop ────────────────────────────────────────────────
// React Router sahifa almashganda scroll pozitsiyasini o'zi
// saqlamaydi — masalan "Mavzular"da pastga tushib, "Orqaga" bossa,
// Dashboard eski scroll holatida ochilib qolardi. Har bir sahifa
// o'zgarishida scrollni yuqoriga qaytaramiz (desktop va mobil uchun).
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// ─── App ────────────────────────────────────────────────────────
const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <LangProvider>
        <FriendDuelProvider>
        <ScrollToTop />
        <ServerDownOverlay />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: "#111827",
              color: "#fff",
              border: "1px solid #1E2D45",
              fontSize: 13,
              borderRadius: 10,
              fontFamily: "Plus Jakarta Sans",
            },
            success: { iconTheme: { primary: "#22C55E", secondary: "#fff" } },
            error: { iconTheme: { primary: "#EF4444", secondary: "#fff" } },
          }}
        />
        <FriendInviteNotification />
        <InstallAppPrompt />
        <TeacherDrawOverlay />
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/oauth" element={<OAuthPage />} />
          <Route path="/" element={<RootRedirect />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/topics"
            element={
              <PrivateRoute>
                <TopicsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <PrivateRoute>
                <TicketsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/mistakes"
            element={
              <PrivateRoute>
                <MistakesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/signs"
            element={
              <PrivateRoute>
                <SignsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/custom"
            element={
              <PrivateRoute>
                <CustomTestPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <PrivateRoute>
                <FavoritesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/history"
            element={
              <PrivateRoute>
                <HistoryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/sessions"
            element={
              <PrivateRoute>
                <SessionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute>
                <PaymentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/roadmap"
            element={
              <PrivateRoute>
                <RoadmapPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/quiz/:mode/:categoryId/:subId"
            element={
              <PrivateRoute>
                <QuizPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/quiz/:mode/:id"
            element={
              <PrivateRoute>
                <QuizPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/quiz/:mode"
            element={
              <PrivateRoute>
                <QuizPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/time-attack"
            element={
              <PrivateRoute>
                <TimeAttackPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/duel"
            element={
              <PrivateRoute>
                <DuelPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tests"
            element={
              <PrivateRoute>
                <TestsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/games"
            element={
              <PrivateRoute>
                <GamesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/learn"
            element={
              <PrivateRoute>
                <LearnHubPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/knowledge"
            element={
              <PrivateRoute>
                <KnowledgePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <PrivateRoute>
                <ContactsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/teachers"
            element={
              <PrivateRoute>
                <TeachersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/shpargalka"
            element={
              <PrivateRoute>
                <ShpargalkaPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher-panel"
            element={
              <PrivateRoute>
                <TeacherPanelPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/friend-duel"
            element={
              <PrivateRoute>
                <FriendDuelPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/ai-ustoz"
            element={
              <PrivateRoute>
                <AiUstozInsightsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/videos"
            element={
              <PrivateRoute>
                <VideosPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/statistics"
            element={
              <PrivateRoute>
                <StatisticsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/ai-ustoz/chat"
            element={
              <PrivateRoute>
                <AiUstozChatPage />
              </PrivateRoute>
            }
          />
        </Routes>
        </FriendDuelProvider>
      </LangProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
